import React from 'react';
import { ReactFlowProvider } from 'reactflow';
import EntityCard from './EntityCard';
import WorkflowExecutionTable from './workflowExecutionTable';
import SIMComponent from './SIMComponent';
import queryString from 'query-string';
import { SNOW_NXT_WORKFLOW_ENDPOINT } from 'src/config';
import { isUserPresentInBindle, sortedTaskStatus } from 'src/utils/CommonUtils';
import katalMetricsPublisher from 'src/utils/metrics';
const taskMetricsPublisher =
	katalMetricsPublisher.newChildActionPublisherForMethod('EntityStatus');

import * as KatalMetrics from '@amzn/katal-metrics';
import { callSNOWDataService } from 'src/utils/snowDataServiceWrapper';
import { logger } from 'src/utils/logger';
import Link from '@amzn/awsui-components-console/link';
import Tabs from '@amzn/awsui-components-console/tabs';
import Box from '@amzn/awsui-components-console/box';
import SpaceBetween from '@amzn/awsui-components-console/space-between';
import { ValidationExecutionStatus, ValidationReport, ValidationResult } from 'src/@types/validationTypes';
import Flashbar from '@amzn/awsui-components-console/flashbar';
import Header from '@amzn/awsui-components-console/header';

import { STATUSES_WITHOUT_EXECUTION_DATA, snowIssueTemplate } from 'src/constants/CommonConstants';
import { ResilienceEntityData } from '../ResilienceWorkflow/ResilienceEntityData';
import WorkflowExecutionCanvas from '../selfServeWorkflows/executionView/workflowExecutionCanvas';
import { OLAF_WORKFLOW_BINDLE_DATA } from 'src/authentication/bindle-utils';
import { AuthContext } from 'src/providers/AuthContext';
import { withRouter, RouteComponentProps } from 'react-router-dom';
import { getAwsCredentials } from 'src/authentication/cognito';

import { AMAZON_APPROVAL_ENDPOINT } from 'src/config';
import { getPhoneToolProfileURL } from 'src/pages/entitySearch/entitySearchConstants';
import { WorkflowStatus } from 'src/constants/commonEnums';
import TaskExecutionSummary from 'src/components/findEntity/TaskExecutionSummary';
import { ListTaskExecutionsResponse } from 'src/@types/selfServeTypes/listTaskExecutions.interface';
import AWS from 'aws-sdk';
import {
	EXECUTED_TASK_STATUSES,
	WAITING_TASK_STATUSES,
	STATUS_TAB_MAP,
	SIM_TASK_TYPES,
} from './findEntityUtils';
import CopyToNew from './CopyToNew';
import ValidationRecord from './ValidationRecord';
import { isLegacyWorkflow } from 'src/components/ExecuteWorkflows/ExecuteWorkflowConstants';
import { isStartedByParent } from 'src/utils/workflowFilterUtils';

interface EntityData {
    WorkflowType: string;
    CycleId: string;
    Status: string;
    LastStatusUpdateTime?: string;
    workflowDefinitionName: string;
    EntityData?: {
        Approver?: string;
    };
    EntityAttributes?: {
        ApprovalId?: string;
        startedByParent?: string;
    };
    [key: string]: unknown;
}

interface TaskExecution {
    status: string;
    entities: string[] | undefined;
    taskType: string;
    taskExecutionId: string;
    taskId: string;
    taskName: string;
    [key: string]: unknown;
}

import { FlashbarProps } from '@amzn/awsui-components-console/flashbar';

interface TabItem {
    label: string;
    id: string;
    content: React.ReactNode;
}

interface EntityStatusOwnProps {
    entityName: string;
    entityData: EntityData;
    isStack: boolean;
    workflowDefinitionName: string;
    validationExecutionId?: string;
    validationStatus?: string;
    validationMessage?: string;
    isValidationPolling?: boolean;
    workflowExecutionError?: string;
    validationReport?: ValidationReport;
    validationResults?: ValidationResult[];
}

type EntityStatusProps = EntityStatusOwnProps & RouteComponentProps;

interface EntityStatusState {
    entityName: string;
    entityData: EntityData;
    taskExecutionData: TaskExecution[] | null;
    status: string | null;
    flashBarItems: FlashbarProps.MessageDefinition[];
    currentTab: string;
    hasExecutePermissions: boolean;
    statusFilter: string | null;
    listTaskExecutionsResponse?: ListTaskExecutionsResponse;
	isLoading: boolean;
}

interface AwsCredentials {
    accessKey: string;
    secretKey: string;
    sessionToken: string;
}

interface ApprovalMessage {
    statusMessage: React.ReactNode;
    approverInfo?: React.ReactNode;
}

declare global {
    interface Window {
        taskExecutions: TaskExecution[];
    }
}

class EntityStatus extends React.Component<EntityStatusProps, EntityStatusState> {
	static contextType = AuthContext;

	context!: React.ContextType<typeof AuthContext>;

	constructor(props: EntityStatusProps) {
		super(props);
		this.state = {
			entityName: props.entityName,
			entityData: props.entityData,
			taskExecutionData: null,
			status: null,
			flashBarItems: [
                    {
                            type: 'info',
                            loading: true,
                            header: 'Loading entity status and task execution data...',
                            dismissible: false,
                            id: 'loading-entity-data'
                    }
            ],
			currentTab: queryString.parse(this.props.location.search).entityStatusTab as string
				? queryString.parse(this.props.location.search).entityStatusTab as string
				: 'entityData',
			hasExecutePermissions: false,
			statusFilter: null,
			isLoading: true,
		};
		this.filterItems = this.filterItems.bind(this);
		this.filterSIMTasks = this.filterSIMTasks.bind(this);
		this.getEntityStatus = this.getEntityStatus.bind(this);
		this.fetchTaskExecutionData = this.fetchTaskExecutionData.bind(this);
		this.setFlashBarItems = this.setFlashBarItems.bind(this);
	}

	async componentDidMount(): Promise<void> {
		await getAwsCredentials();
		const credentials: AwsCredentials = {
			accessKey: AWS.config.credentials?.accessKeyId || '',
			secretKey: AWS.config.credentials?.secretAccessKey || '',
			sessionToken: AWS.config.credentials?.sessionToken || '',
		};

		await this.isUserHaveExecutePermissions(credentials);
		this.fetchTaskExecutionData(credentials);
		
		if (this.props.validationExecutionId) {
			this.updateValidationFlashbar();
		}
	}

	componentDidUpdate(prevProps: EntityStatusProps): void {
		if (
			prevProps.validationStatus !== this.props.validationStatus ||
			prevProps.isValidationPolling !== this.props.isValidationPolling ||
			prevProps.validationExecutionId !== this.props.validationExecutionId ||
			prevProps.workflowExecutionError !== this.props.workflowExecutionError
		) {
			if (this.props.workflowExecutionError) {
				this.setState({
					flashBarItems: [
						{
							header: 'Failed to start workflow execution',
							type: 'error',
							content: <p>Validation passed but workflow execution failed to start. Error: {this.props.workflowExecutionError}. Please reach out to SNOW Team. Issue <a href={snowIssueTemplate} target="_blank" rel="noopener noreferrer">link</a></p>,
							dismissible: true,
						},
					],
				});
			} else {
				this.updateValidationFlashbar();
			}
		}
	}

	updateValidationFlashbar(): void {
		if (!this.props.validationExecutionId) {
			return;
		}

		if (!this.props.validationStatus) {
			return;
		}

		const status = this.props.validationStatus;
		const isFailed =
			status === ValidationExecutionStatus.VALIDATION_FAILED ||
			status === ValidationExecutionStatus.FAILED ||
			status === ValidationExecutionStatus.INVALID;
		const isPassed =
			status === ValidationExecutionStatus.VALIDATION_PASSED ||
			status === ValidationExecutionStatus.VALID;
		const isFatal = status === ValidationExecutionStatus.VALIDATION_FATAL;

		// Evaluate terminal states before the in-progress check so a stale
		// "polling" flag can't override a finalized FAILED/PASSED/FATAL result.
		if (isPassed) {
			this.setState({
				flashBarItems: [],
			});
		} else if (isFailed) {
			const executeWorkflowUrl = `${window.location.origin}${window.location.pathname}#/executeWorkflow/${this.state.entityData.WorkflowType}?entityName=${encodeURIComponent(this.state.entityName)}`;
			this.setState({
				flashBarItems: [
					{
						header: 'Validation failed',
						content: <>Validation failed. See validation results in the Validation Record tab for details. <a href={executeWorkflowUrl}>Click here</a> to update the workflow execution.</>,
						type: 'error',
						dismissible: true,
					},
				],
			});
		} else if (isFatal) {
			this.setState({
				flashBarItems: [
					{
						header: 'Validation fatal error',
						content: <>Validation encountered a fatal error. See validation results in the Validation Record tab for details.</>,
						type: 'error',
						dismissible: true,
					},
				],
			});
		} else if (this.props.isValidationPolling || status === ValidationExecutionStatus.VALIDATION_IN_PROGRESS) {
			this.setState({
				flashBarItems: [
					{
						header: 'Validation in progress',
						content: <>Workflow has not yet started for {this.state.entityName}. Validation is in progress...</>,
						type: 'info',
						dismissible: false,
					},
				],
			});
		}
	}

	async isUserHaveExecutePermissions(credentials: AwsCredentials): Promise<void> {
		const userName = this.context.userName;

		try {
			await isUserPresentInBindle(
				this.state.entityData.WorkflowType,
				userName,
				OLAF_WORKFLOW_BINDLE_DATA.type,
				OLAF_WORKFLOW_BINDLE_DATA.action,
				OLAF_WORKFLOW_BINDLE_DATA.namespace,
				credentials
			);
			this.setState({ hasExecutePermissions: true });
		} catch (error) {
			this.setState({ hasExecutePermissions: false });
		}
	}

	async refreshTaskData(): Promise<void> {
		try {
			await getAwsCredentials();
			const credentials: AwsCredentials = {
				accessKey: AWS.config.credentials?.accessKeyId || '',
				secretKey: AWS.config.credentials?.secretAccessKey || '',
				sessionToken: AWS.config.credentials?.sessionToken || '',
			};

			const resData = await callSNOWDataService({
				path: `/workflows/${this.state.entityData.WorkflowType}/executions/${this.state.entityData.CycleId}/tasks`,
				method: 'GET',
				url: SNOW_NXT_WORKFLOW_ENDPOINT,
				credentials: credentials as Parameters<typeof callSNOWDataService>[0]['credentials'],
				body: null,
			});

			this.setState({
				taskExecutionData: resData.taskExecutions,
				listTaskExecutionsResponse: resData,
			});
		} catch (e) {
			logger.error(`Error refreshing task data: ${JSON.stringify(e)}`);
		}
	}

	async fetchTaskExecutionData(credentials: AwsCredentials): Promise<void> {
		const taskMetric = new KatalMetrics.Metric.TimedAttempt(
			'getTaskExecutionData'
		).withMonitor();

		const taskExecutionDataURL = `/workflows/${this.state.entityData.WorkflowType}/executions/${this.state.entityData.CycleId}/tasks`;

		logger.info(
			`Fetching the task Data [ Enitity : taskExecutionDataURL ] ::: [ ${this.state.entityName} : ${taskExecutionDataURL} ]`
		);

		try {
			const resData = await callSNOWDataService({
				path: taskExecutionDataURL,
				method: 'GET',
				url: SNOW_NXT_WORKFLOW_ENDPOINT,
				credentials: credentials as Parameters<typeof callSNOWDataService>[0]['credentials'],
				body: null,
			});

			logger.info(
				`Got Task Details [ Enitity : Workflow Name : Exectuions ] ::: [ ${this.state.entityName} : ${this.state.entityData.WorkflowType} : ${resData.taskExecutions} ]`
			);
			window.taskExecutions = resData.taskExecutions;
			taskMetric.setSuccess();
			this.setState({
				taskExecutionData: resData.taskExecutions,
				listTaskExecutionsResponse: resData,
				status: this.getEntityStatus(),
				isLoading: false,
			});

			if (this.props.validationExecutionId) {
				this.updateValidationFlashbar();
			} else if (resData.taskExecutions?.length === 0) {
				this.setState({
					flashBarItems: [
						{
							header: 'No task execution data available',
							type: 'warning',
							content: 'The workflow is marked as completed but there are no executed tasks for this entity.',
							dismissible: true,
						}
					],
				});
			} else {
				this.setState({ flashBarItems: [] });
			}
		} catch (e) {
			logger.error(
				`API Gateway :- ERROR in dashboard AJAX request while requesting task details [ Enitity : Error ] ::: [ ${
					this.state.entityName
				} : ${JSON.stringify(e)} ]`
			);
			taskMetric.setFailure();

			this.setState({
				status: this.getEntityStatus(), 
				isLoading: false,
			});

			if (this.state.status && !STATUSES_WITHOUT_EXECUTION_DATA.includes(this.state.status)) {
				this.setState({
					flashBarItems: [
						{
							header: `Failed to fetch task execution data of ${this.state.entityName}`,
							type: 'error',
							content: (
								<p>
                                    Please reach out to SNOW Team. Issue{' '}
									<a href={snowIssueTemplate} target="_blank" rel="noreferrer">
                                        link
									</a>
								</p>
							),
							dismissible: true,
						},
					],
				});
			} else {
				if (this.props.validationExecutionId) {
					this.updateValidationFlashbar();
				} else {
					const approvalMessage = this.getApprovalMessage(this.state.entityData);
					const { statusMessage, approverInfo } = approvalMessage || { statusMessage: null, approverInfo: null };

					if (statusMessage || approverInfo) {
						const isDraftEditable = this.state.entityData.Status === WorkflowStatus.DRAFT
							&& !isLegacyWorkflow(this.state.entityData.WorkflowType)
							&& !isStartedByParent(this.state.entityData.EntityAttributes)
							&& this.state.hasExecutePermissions;

						this.setState({
							flashBarItems: [
								{
									header: isDraftEditable ? 'Node is in Draft' : 'Workflow has not been started yet',
									content: isDraftEditable ? (
										<>
											<Link href={`#/executeWorkflow/${this.state.entityData.WorkflowType}?entityName=${encodeURIComponent(this.state.entityName)}`} external>
												Click here
											</Link>
											{' '}to edit the draft on the Workflow Execution requirement page.
										</>
									) : (
										<>
											{statusMessage}
											{approverInfo && <div>{approverInfo}</div>}
										</>
									),
									type: 'info',
									dismissible: true,
									id: isDraftEditable ? 'draft-edit-prompt' : undefined,
								},
							],
						});
					} else {
						this.setState({
							flashBarItems: [],
						});
					}
				}
			}
		} finally {
			taskMetricsPublisher.publish(taskMetric);
		}
	}

	getApprovalMessage(entityData: EntityData): ApprovalMessage | undefined {
		const { EntityData: entityDataInner, EntityAttributes, Status } = entityData;
		const Approver = entityDataInner?.Approver;
		const ApprovalId = EntityAttributes?.ApprovalId;

		if (Status === WorkflowStatus.PENDING_APPROVAL || Status === WorkflowStatus.SUBMITTED) {
			return {
				statusMessage: ApprovalId
					? <> This workflow execution is currently pending approval on this approval request : <Link color='inverted' href={`${AMAZON_APPROVAL_ENDPOINT}/Approval/Details/${ApprovalId}`} external>{ApprovalId}</Link> </>
					: <></>,
				approverInfo: Approver
					? <> This workflow execution is awaiting approval from <Link color='inverted' href={getPhoneToolProfileURL(Approver)} external>@{Approver}</Link></>
					: <></>,
			};
		} else if (Status === WorkflowStatus.DRAFT) {
			if (this.props.validationExecutionId) {
				// When a validation is in flight or already finished, let
				// updateValidationFlashbar own the banner so we don't override
				// a terminal status (FAILED/FATAL) with a stale "in progress" message.
				return undefined;
			}

			return { 
				statusMessage: <>The Workflow Execution Data is yet to be submitted and is in DRAFT state.</>,
				approverInfo: <></>,
			};
		}

		return undefined;
	}

	render(): JSX.Element {
		let tabs: TabItem[] = this.props.isStack
			? [
				{
					label: 'Entity Data',
					id: 'entityData',
					content: (
						<ResilienceEntityData
							status={this.state.status || ''}
							entityData={this.state.entityData}
							entityName={this.state.entityName}
						/>
					),
				},
			]
			: [
				{
					label: 'Entity Data',
					id: 'entityData',
					content: (
						<EntityCard
							status={this.state.status || ''}
							entityData={this.state.entityData}
							lastStatusUpdateTime={
								this.state.entityData &&
                                    this.state.entityData.LastStatusUpdateTime
									? this.state.entityData.LastStatusUpdateTime
									: ''
							}
							workflowType={this.state.entityData.WorkflowType}
							workflowDefinitionName={this.props.entityData.workflowDefinitionName}
							taskExecutionData={this.state.taskExecutionData}
							finishedTasks={this.state.taskExecutionData ? this.filterItems(this.state.taskExecutionData, ['COMPLETED', 'SKIPPED', 'OVERRIDEN']).length : 0}
							totalTasks={this.state.taskExecutionData ? this.state.taskExecutionData.filter(
								(item) =>
									item.entities != null &&
									item.entities.includes(this.state.entityName)
							).length : 0}
						/>
					),
				},
			];

		// To check if entity is in progress and tasks are populated
		if (
			this.state.taskExecutionData !== undefined &&
            this.state.taskExecutionData !== null &&
            this.state.taskExecutionData.length > 0
		) {
			const executedTasks = this.filterItems(this.state.taskExecutionData, EXECUTED_TASK_STATUSES as unknown as string[]);
			const waitingTasks = this.filterItems(this.state.taskExecutionData, WAITING_TASK_STATUSES as unknown as string[]);
			const simTasks = this.filterSIMTasks(this.state.taskExecutionData);

			tabs = [
				...tabs,
				{
					label: 'Executed Tasks',
					id: 'executedTasks',
					content: (
						<WorkflowExecutionTable
							key={`executed-tasks-${this.state.statusFilter || 'all'}`}
							taskExecutionData={executedTasks.sort((a, b) => {
								return -sortedTaskStatus(a.status, b.status);
							})}
							workflowId={this.state.entityData.WorkflowType}
							workflowExecutionId={this.state.entityData.CycleId}
							entityName={this.state.entityName}
							show={true}

							refreshView={() => this.refreshTaskData()}
							setFlashBarItems={this.setFlashBarItems}
							hasExecutePermissions={this.state.hasExecutePermissions}
							statusFilter={this.state.statusFilter}
						/>
					),
				},
				{
					label: 'Graphical Task Execution View',
					id: 'executionView',
					content: (
						<ReactFlowProvider>
							<WorkflowExecutionCanvas
								workflowId={this.state.entityData.WorkflowType}
								workflowExecutionId={this.state.entityData.CycleId}
								listTaskExecutionsResponse={
									this.state.listTaskExecutionsResponse || { taskExecutions: [], dependencies: [] }
								}
								nodeName={this.state.entityName}
								hasExecutePermissions={this.state.hasExecutePermissions}
								setFlashBarItems={this.setFlashBarItems}
								refreshView={() => this.refreshTaskData()}
							/>
						</ReactFlowProvider>
					),
				},
				{
					label: 'Waiting Tasks',
					id: 'Waiting',
					content: (
						<WorkflowExecutionTable
							workflowId={this.state.entityData.WorkflowType}
							workflowExecutionId={this.state.entityData.CycleId}
							taskExecutionData={waitingTasks}
							show={true}
							entityName={this.state.entityName}
							hasExecutePermissions={this.state.hasExecutePermissions}
							tableTitle="Waiting/Scheduled Tasks"
						/>
					),
				},
				{
					label: 'SIM / TT Tracking',
					id: 'simTracking',
					content: (
						<SIMComponent
							simTasks={simTasks}
							workflowName={this.state.entityData.WorkflowType}
						/>
					),
				},
			];
		}

		// Append the "Validation Record" tab whenever validation data exists for this entity
		const hasValidationData = Boolean(
			this.props.validationExecutionId ||
			this.props.validationStatus ||
			this.props.validationReport ||
			(this.props.validationResults && this.props.validationResults.length > 0)
		);

		if (hasValidationData) {
			tabs = [
				...tabs,
				{
					label: 'Validation Record',
					id: 'validationRecord',
					content: (
						<ValidationRecord
							validationExecutionId={this.props.validationExecutionId}
							validationStatus={this.props.validationStatus}
							validationMessage={this.props.validationMessage}
							isValidationPolling={this.props.isValidationPolling}
							validationReport={this.props.validationReport}
							validationResults={this.props.validationResults}
						/>
					),
				},
			];
		}

		return (
			<Box>
				<SpaceBetween size="s">
					<Header
						variant="h1"
						actions={
							this.state.status === 'COMPLETED' && this.state.hasExecutePermissions && !isLegacyWorkflow(this.state.entityData.WorkflowType)
								&& !isStartedByParent(this.state.entityData.EntityAttributes) ? (
								<CopyToNew
									sourceEntityName={this.state.entityName}
									workflowType={this.state.entityData.WorkflowType}
									workflowName={this.props.workflowDefinitionName}
									onSuccess={(destName) => {
										const workflowId = this.state.entityData.WorkflowType;

										this.setState({
											flashBarItems: [
												{
													type: 'success',
													header: 'Draft created successfully',
													content: (
														<>
															&quot;{destName}&quot; has been drafted successfully.{' '}
															<Link href={`#/executeWorkflow/${workflowId}?entityName=${encodeURIComponent(destName)}`} external>
																Click here
															</Link>
															{' '}to edit the draft in Workflow Execution.
														</>
													),
													dismissible: true,
													onDismiss: () => this.setState({ flashBarItems: [] }),
													id: 'copy-to-new-success',
												},
												...this.state.flashBarItems,
											],
										});
									}}
								/>
							) : undefined
						}
					>
						{this.props.workflowDefinitionName} - {this.state.entityName}
					</Header>
					<Flashbar items={this.state.flashBarItems} />
					{this.state.taskExecutionData && this.state.taskExecutionData.length > 0 && (
						<TaskExecutionSummary
							taskExecutionData={this.state.taskExecutionData}
							entityName={this.state.entityName}
							onStatusClick={(status: string) => {
								const newTab = STATUS_TAB_MAP[status] || 'executedTasks';
                            
								if (status === 'TOTAL') {
									this.setState({ 
										currentTab: newTab,
										statusFilter: null,
									});
								}
								else if (this.state.statusFilter === status) {
									this.setState({ 
										currentTab: newTab,
										statusFilter: null,
									});
								} 
								else {
									this.setState({ 
										currentTab: newTab,
										statusFilter: status,
									});
								}
							}}
						/>
					)}
					<Tabs
						tabs={tabs}
						variant="default"
						activeTabId={this.state.currentTab}
						onChange={async ({ detail }) => {
							this.setState({
								...this.state,
								currentTab: detail.activeTabId,
								statusFilter: null,
							});

							if (detail.activeTabId === 'executedTasks') {
								await getAwsCredentials();
								const credentials: AwsCredentials = {
									accessKey: AWS.config.credentials?.accessKeyId || '',
									secretKey: AWS.config.credentials?.secretAccessKey || '',
									sessionToken: AWS.config.credentials?.sessionToken || '',
								};

								this.fetchTaskExecutionData(credentials);
							}
						}}
					/>
				</SpaceBetween>
			</Box>
		);
	}

	getEntityStatus(): string {
		return this.state.entityData.Status === 'RUNNING'
			? 'IN PROGRESS'
			: this.state.entityData.Status === 'READY'
				? 'SUBMITTED'
				: this.state.entityData.Status;
	}

	filterItems(Items: TaskExecution[], filterType: string[]): TaskExecution[] {
		return Items.filter(
			(item) =>
				filterType.includes(item.status) &&
                item.entities !== undefined &&
                item.entities !== null &&
                item.entities.includes(this.state.entityName)
		);
	}

	async setFlashBarItems(flashBarItems: FlashbarProps.MessageDefinition[]): Promise<void> {
		this.setState({ flashBarItems });
	}

	filterSIMTasks(Items: TaskExecution[]): TaskExecution[] {
		return Items.filter(
			(item) =>
				SIM_TASK_TYPES.includes(item.taskType as any) &&
                item.entities !== undefined &&
                item.entities !== null &&
                item.entities.includes(this.state.entityName)
		);
	}
}

export default withRouter(EntityStatus);
